import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Target,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  Brain,
  Zap,
  Award,
  Clock
} from 'lucide-react';
import useUserStore from '../../store/userStore';
import ProgressChart from '../../components/Charts/ProgressChart';
import SkillsChart from '../../components/Charts/SkillsChart';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const Analytics = () => {
  const { user } = useUserStore();
  const [selectedPeriod, setSelectedPeriod] = useState('6months');
  const [isLoading, setIsLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch real analytics data
  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { default: api } = await import('../../services/api');
      const response = await api.get('/users/analytics');
      setAnalyticsData(response.data.analytics);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  const userType = user?.user_type || 'learner';

  const periods = [
    { value: '1month', label: 'Last Month' },
    { value: '3months', label: 'Last 3 Months' },
    { value: '6months', label: 'Last 6 Months' },
    { value: '1year', label: 'Last Year' }
  ];

  const handleExport = () => {
    // Export analytics data
    const dataStr = JSON.stringify(analyticsData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `skillmarg_analytics_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleRefresh = () => {
    fetchAnalytics();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={fetchAnalytics} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const getTitle = () => {
    switch (userType) {
      case 'learner': return 'Learning Analytics';
      case 'trainer': return 'Teaching Analytics';
      case 'policymaker': return 'Policy Analytics';
      default: return 'Analytics Dashboard';
    }
  };

  const getDescription = () => {
    switch (userType) {
      case 'learner': return 'Track your learning progress and achievements';
      case 'trainer': return 'Monitor your course performance and student engagement';
      case 'policymaker': return 'Analyze skills development trends and policy impact';
      default: return 'Comprehensive analytics and insights';
    }
  };

  // Generate stats from real data
  const generateStats = () => {
    if (!analyticsData) return [];
    
    switch (userType) {
      case 'learner':
        return [
          {
            title: 'Assessment Score',
            value: analyticsData.current_level || 'Not Assessed',
            trend: analyticsData.recommended_difficulty ? `Recommended: ${analyticsData.recommended_difficulty}` : '',
            color: 'primary'
          },
          {
            title: 'Skills Tracked',
            value: Object.keys(analyticsData.skill_confidence || {}).length,
            trend: '+2 this month',
            color: 'secondary'
          },
          {
            title: 'Courses Viewed',
            value: analyticsData.engagement_stats?.courses_clicked_count || 0,
            trend: '+3 this week',
            color: 'accent'
          },
          {
            title: 'Roadmaps Generated',
            value: analyticsData.engagement_stats?.roadmaps_generated_count || 0,
            trend: '+1 this month',
            color: 'success'
          }
        ];
      case 'trainer':
        return [
          { title: 'Courses Managed', value: 12, trend: '+2 this month', color: 'primary' },
          { title: 'Student Rating', value: '4.7', trend: '+0.2 this month', color: 'secondary' },
          { title: 'Completion Rate', value: '78%', trend: '+3% this month', color: 'accent' },
          { title: 'Revenue', value: '₹125K', trend: '+8% this month', color: 'success' }
        ];
      case 'policymaker':
        return [
          { title: 'Total Learners', value: '12.5K', trend: '+5.2% this month', color: 'primary' },
          { title: 'Active Trainers', value: 450, trend: '+12 this month', color: 'secondary' },
          { title: 'Skills Gap', value: '25%', trend: '+3 this week', color: 'warning' },
          { title: 'Completion Rate', value: '72%', trend: '+2% this month', color: 'success' }
        ];
      default:
        return [];
    }
  };

  // Generate chart data
  const generateChartData = () => {
    if (!analyticsData) return { progress: [], skills: [] };
    
    // Mock progress data based on engagement
    const progressData = [
      { month: 'Jan', completed: analyticsData.engagement_stats?.courses_clicked_count || 2, inProgress: 1 },
      { month: 'Feb', completed: (analyticsData.engagement_stats?.courses_clicked_count || 2) + 2, inProgress: 2 },
      { month: 'Mar', completed: (analyticsData.engagement_stats?.courses_clicked_count || 2) + 4, inProgress: 1 },
      { month: 'Apr', completed: (analyticsData.engagement_stats?.courses_clicked_count || 2) + 6, inProgress: 3 },
      { month: 'May', completed: (analyticsData.engagement_stats?.courses_clicked_count || 2) + 8, inProgress: 2 },
      { month: 'Jun', completed: analyticsData.engagement_stats?.courses_clicked_count || 10, inProgress: 3 }
    ];

    // Skills data from skill confidence
    const skillsData = Object.entries(analyticsData.skill_confidence || {}).map(([skill, confidence]) => ({
      name: skill,
      level: confidence * 20, // Convert 0-5 scale to 0-100
      trend: 'stable'
    }));

    return { progress: progressData, skills: skillsData };
  };

  const stats = generateStats();
  const chartData = generateChartData();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{getTitle()}</h1>
          <p className="text-gray-600 mt-2">{getDescription()}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="btn btn-outline btn-sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="btn btn-primary btn-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="card">
        <div className="card-content">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Time Period:</span>
            </div>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="input text-sm"
            >
              {periods.map(period => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="card hover-lift">
              <div className="card-content">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                    <p className="text-sm text-gray-500 mt-1">{stat.trend}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${
                    stat.color === 'primary' ? 'bg-primary-100 text-primary-600' :
                    stat.color === 'secondary' ? 'bg-secondary-100 text-secondary-600' :
                    stat.color === 'accent' ? 'bg-accent-100 text-accent-600' :
                    'bg-green-100 text-green-600'
                  }`}>
                    <BarChart3 className="h-6 w-6" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="card-title">
              {userType === 'learner' ? 'Learning Progress' : 
               userType === 'trainer' ? 'Course Performance' : 
               'Skills Development Trends'}
            </h3>
            <p className="card-description">
              {userType === 'learner' ? 'Your course completion over time' :
               userType === 'trainer' ? 'Enrollments and completions over time' :
               'Overall skills development trends'}
            </p>
          </div>
          <div className="card-content">
            <ProgressChart data={chartData.progress} />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="card-title">
              {userType === 'learner' ? 'Skill Confidence' :
               userType === 'trainer' ? 'Popular Skills' :
               'Skills Gap Analysis'}
            </h3>
            <p className="card-description">
              {userType === 'learner' ? 'Your self-assessed skill confidence' :
               userType === 'trainer' ? 'Skills your students are learning' :
               'Critical skills requiring attention'}
            </p>
          </div>
          <div className="card-content">
            <SkillsChart data={chartData.skills} />
          </div>
        </motion.div>
      </div>

      {/* Learner-Specific Analytics */}
      {userType === 'learner' && analyticsData && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Assessment Results */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card"
          >
            <div className="card-header">
              <h3 className="card-title flex items-center gap-2">
                <Brain className="h-5 w-5" />
                Assessment Results
              </h3>
            </div>
            <div className="card-content">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600 mb-1">
                    {analyticsData.current_level || 'Not Assessed'}
                  </div>
                  <div className="text-sm text-gray-600">Current Level</div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Learning Speed</span>
                    <span className="font-medium">{analyticsData.ai_attributes?.learning_speed || 'Average'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Memory Strength</span>
                    <span className="font-medium">{analyticsData.ai_attributes?.memory_strength || 'Average'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Difficulty Tolerance</span>
                    <span className="font-medium">{analyticsData.ai_attributes?.difficulty_tolerance || 'Medium'}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Engagement Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="card"
          >
            <div className="card-header">
              <h3 className="card-title flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Learning Engagement
              </h3>
            </div>
            <div className="card-content">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-xl font-bold text-blue-600">
                      {analyticsData.engagement_stats?.courses_clicked_count || 0}
                    </div>
                    <div className="text-xs text-gray-600">Courses Viewed</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xl font-bold text-green-600">
                      {analyticsData.engagement_stats?.roadmaps_generated_count || 0}
                    </div>
                    <div className="text-xs text-gray-600">Roadmaps</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-xl font-bold text-purple-600">
                      {analyticsData.engagement_stats?.active_days_count || 0}
                    </div>
                    <div className="text-xs text-gray-600">Active Days</div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="text-xl font-bold text-orange-600">
                      {analyticsData.engagement_stats?.avg_session_duration?.toFixed(1) || 0}
                    </div>
                    <div className="text-xs text-gray-600">Avg Session (min)</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* AI Insights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="card"
          >
            <div className="card-header">
              <h3 className="card-title flex items-center gap-2">
                <Target className="h-5 w-5" />
                AI Insights
              </h3>
            </div>
            <div className="card-content">
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg">
                  <h4 className="font-medium mb-2">Career Direction</h4>
                  <p className="text-sm text-gray-700">
                    {analyticsData.inferred_interests?.career_direction || 'Not enough data yet'}
                  </p>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Top Skills of Interest</h4>
                  <div className="flex flex-wrap gap-2">
                    {(analyticsData.inferred_interests?.skills_of_interest || []).slice(0, 3).map((skill, idx) => (
                      <span key={idx} className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Learning Strengths</h4>
                  <div className="text-sm text-gray-700">
                    {(analyticsData.inferred_interests?.strengths || ['Not analyzed yet'])[0]}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <LoadingSpinner size="md" />
            <span className="text-gray-700">Refreshing analytics...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;